/**
 * Webhook Adapter - Submit form data to any webhook endpoint
 *
 * A generic adapter for sending form data to any HTTP endpoint.
 *
 * @example
 * const adapter = createWebhookAdapter({
 *   url: 'https://api.example.com/forms',
 *   headers: { 'Authorization': 'Bearer token' }
 * });
 * await adapter.submit(formData, context);
 */

import type {
  FormData,
  SubmissionContext,
  SubmissionResult,
  SubmissionAdapter,
  WebhookConfig,
} from './types.js';

/**
 * Create a webhook submission adapter
 */
export function createWebhookAdapter(config: WebhookConfig): SubmissionAdapter {
  const { url, method = 'POST', headers = {}, fieldMapping = {}, mock = false } = config;

  return {
    name: 'webhook',

    mapFields(data: FormData): FormData {
      const mapped: FormData = {};
      for (const [key, value] of Object.entries(data)) {
        const mappedKey = fieldMapping[key] || key;
        mapped[mappedKey] = value;
      }
      return mapped;
    },

    async submit(data: FormData, context: SubmissionContext): Promise<SubmissionResult> {
      // Mock mode for testing
      if (mock) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        return { success: true, data: { mock: true, formData: data } };
      }

      if (!url) {
        return { success: false, error: 'Webhook URL is required' };
      }

      const mappedData = this.mapFields(data);

      const payload = {
        formData: mappedData,
        context: {
          pageUrl: context.pageUrl,
          pageTitle: context.pageTitle,
          referrer: context.referrer,
          submittedAt: context.timestamp,
        },
      };

      try {
        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            ...headers,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const errorText = await response.text().catch(() => '');
          return {
            success: false,
            error: `Webhook failed: ${response.status} ${errorText}`.trim(),
          };
        }

        // Try to parse JSON response, fall back to success with no data
        let responseData: unknown;
        try {
          responseData = await response.json();
        } catch {
          responseData = { status: response.status };
        }

        return { success: true, data: responseData };
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Network error';
        return { success: false, error: errorMessage };
      }
    },
  };
}

export type { WebhookConfig };
